﻿if (!customElements.get('opt-trust-carousel')) {
  customElements.define('opt-trust-carousel', class extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.opt-trust-bar');
      this.items = Array.from(this.track?.children || []);
      if (this.items.length < 2) return;
      this.abort = new AbortController();
      const options = { signal: this.abort.signal };
      this.mobile = matchMedia('(max-width: 767px)');
      this.reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
      this.visible = false;
      this.touching = false;
      this.focused = false;
      this.hovered = false;
      this.clones = [];
      this.phase = 0;
      this.tick = this.tick.bind(this);
      this.mobile.addEventListener('change', () => this.rebuild(), options);
      this.reducedMotion.addEventListener('change', () => this.rebuild(), options);
      document.addEventListener('visibilitychange', () => this.update(), options);
      this.addEventListener('pointerenter', event => {
        if (event.pointerType === 'mouse') { this.hovered = true; this.update(); }
      }, options);
      this.addEventListener('pointerleave', () => { this.hovered = false; this.update(); }, options);
      this.addEventListener('focusin', () => { this.focused = true; this.update(); }, options);
      this.addEventListener('focusout', event => {
        this.focused = this.contains(event.relatedTarget);
        this.update();
      }, options);
      this.track.addEventListener('touchstart', () => { this.touching = true; this.update(); }, { ...options, passive: true });
      for (const event of ['touchend', 'touchcancel']) {
        this.track.addEventListener(event, () => { this.touching = false; this.update(); }, options);
      }
      this.observer = new IntersectionObserver(entries => {
        this.visible = entries[0].isIntersecting;
        this.update();
      });
      this.observer.observe(this);
      this.resizeObserver = new ResizeObserver(() => this.rebuild());
      this.resizeObserver.observe(this.track);
      this.rebuild();
      document.fonts?.ready.then(() => { if (this.isConnected) this.rebuild(); });
    }

    rebuild() {
      cancelAnimationFrame(this.frame);
      this.clones.forEach(clone => clone.remove());
      this.clones = [];
      this.distance = 0;
      if (this.mobile.matches && !this.reducedMotion.matches) {
        this.distance = this.items.reduce((sum, item) => sum + item.getBoundingClientRect().width, 0);
        if (this.distance > 0) {
          // Fill the viewport with repeats even as the last original leaves it.
          const repeats = Math.ceil(this.track.clientWidth / this.distance) + 1;
          for (let repeat = 0; repeat < repeats; repeat++) {
            for (const item of this.items) {
              const clone = item.cloneNode(true);
              clone.setAttribute('aria-hidden', 'true');
              clone.setAttribute('inert', '');
              for (const node of [clone, ...clone.querySelectorAll('*')]) {
                node.removeAttribute('id');
                node.removeAttribute('data-shopify-editor-block');
              }
              this.track.appendChild(clone);
              this.clones.push(clone);
            }
          }
        }
      }
      this.direction = getComputedStyle(this.track).direction === 'rtl' ? -1 : 1;
      this.phase = this.distance ? this.phase % this.distance : 0;
      this.track.scrollLeft = this.direction * this.phase;
      this.update();
    }

    update() {
      cancelAnimationFrame(this.frame);
      this.lastTime = null;
      if (!this.distance || !this.mobile.matches || this.reducedMotion.matches || !this.visible || this.touching || this.focused || this.hovered || document.hidden) return;
      this.phase = Math.abs(this.track.scrollLeft) % this.distance;
      this.frame = requestAnimationFrame(this.tick);
    }

    tick(time) {
      if (this.lastTime !== null) {
        // Preserve fractional pixels so scroll rounding cannot change the speed.
        const elapsed = Math.min(time - this.lastTime, 64);
        this.phase = (this.phase + elapsed * 0.032) % this.distance;
        this.track.scrollLeft = this.direction * this.phase;
      }
      this.lastTime = time;
      this.frame = requestAnimationFrame(this.tick);
    }

    disconnectedCallback() {
      cancelAnimationFrame(this.frame);
      this.abort?.abort();
      this.observer?.disconnect();
      this.resizeObserver?.disconnect();
      this.clones?.forEach(clone => clone.remove());
    }
  });
}
